import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import EventCard from '../components/EventCard';
import EmailVerificationBanner from '../components/EmailVerificationBanner';
import { 
  Plus, 
  Calendar, 
  DollarSign, 
  Ticket, 
  TrendingUp,
  BarChart3,
  Users,
  Eye,
  Settings,
  ExternalLink,
  MessageSquare,
  Send,
  Clock,
  MapPin,
  Edit,
  Trash2,
  Copy,
  Globe,
  Lock,
  ChevronRight,
  Megaphone,
  Inbox,
  PieChart,
  LineChart,
  Wallet,
  CheckCircle,
  XCircle,
  AlertCircle,
  Tag,
  Percent,
  Instagram,
  Youtube,
  Twitter,
  Sparkles,
  Shield,
  Building,
  Target,
  RefreshCw,
  QrCode,
  UserPlus,
  Mail,
  Gavel,
  Banknote,
  ArrowRight,
  History
} from 'lucide-react';
import { format } from 'date-fns';
import { 
  LineChart as RechartsLineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  Area,
  AreaChart
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CHART_COLORS = ['#8b5cf6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#ec4899'];

// TikTok icon component
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

export default function InfluencerDashboard() {
  const navigate = useNavigate();
  const { user, isAuthenticated, isInfluencer, loading: authLoading, refreshUser } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState({
    total_events: 0,
    published_events: 0,
    draft_events: 0,
    total_tickets_sold: 0,
    total_revenue: 0,
    net_revenue: 0,
    pending_payout: 0,
    total_followers: 0
  });
  const [events, setEvents] = useState([]);
  const [recentTickets, setRecentTickets] = useState([]);
  const [recentBids, setRecentBids] = useState([]);
  const [auctionStats, setAuctionStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showBroadcastModal, setShowBroadcastModal] = useState(false);
  const [broadcastMessage, setBroadcastMessage] = useState('');
  const [sendingBroadcast, setSendingBroadcast] = useState(false);
  
  // Analytics state
  const [analyticsData, setAnalyticsData] = useState(null);
  const [audienceData, setAudienceData] = useState(null);
  const [analyticsPeriod, setAnalyticsPeriod] = useState('30');
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  
  // Venue Intelligence state
  const [venueData, setVenueData] = useState(null);
  const [loadingVenue, setLoadingVenue] = useState(false);
  
  // Promo Codes state
  const [promoCodes, setPromoCodes] = useState([]);
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [newPromo, setNewPromo] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: 10,
    max_uses: '',
    expires_at: '',
    event_id: ''
  });
  const [creatingPromo, setCreatingPromo] = useState(false);
  
  // Social Verification state
  const [socialHandles, setSocialHandles] = useState({});
  const [showSocialModal, setShowSocialModal] = useState(false);
  const [newSocialHandle, setNewSocialHandle] = useState({ platform: 'instagram', handle: '' });
  const [connectingSocial, setConnectingSocial] = useState(false);

  // Team Members state
  const [teamMembers, setTeamMembers] = useState([]);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [newTeamMember, setNewTeamMember] = useState({
    email: '',
    role: 'scanner',
    event_ids: []
  });
  const [invitingMember, setInvitingMember] = useState(false);
  const [editingMember, setEditingMember] = useState(null);

  // Payout state
  const [payoutBalance, setPayoutBalance] = useState(null);
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [payoutRequest, setPayoutRequest] = useState({
    amount: '',
    paypal_email: '',
    payout_type: 'early'
  });
  const [requestingPayout, setRequestingPayout] = useState(false);
  const [loadingPayouts, setLoadingPayouts] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
      return;
    }
    if (!authLoading && isAuthenticated && !isInfluencer) {
      navigate('/influencer-signup');
      return;
    }
    if (isInfluencer) {
      fetchDashboardData();
      fetchPromoCodes();
      fetchSocialHandles();
      fetchTeamMembers();
      fetchPayoutData();
    }
  }, [isAuthenticated, isInfluencer, authLoading, navigate]);

  useEffect(() => {
    if (activeTab === 'analytics' && isInfluencer) {
      fetchAnalytics();
      fetchAudience();
      fetchVenueIntelligence();
    }
    if (activeTab === 'earnings' && isInfluencer) {
      fetchPayoutData();
    }
  }, [activeTab, analyticsPeriod, isInfluencer]);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, eventsRes] = await Promise.all([
        axios.get(`${API_URL}/influencer/stats`),
        axios.get(`${API_URL}/influencer/events`)
      ]);
      setStats(statsRes.data);
      setEvents(eventsRes.data);
      
      // Fetch recent ticket sales
      try {
        const ticketsRes = await axios.get(`${API_URL}/influencer/recent-sales`);
        setRecentTickets(ticketsRes.data || []);
      } catch (e) {
        setRecentTickets([]);
      }
      
      // Fetch recent bids and auction stats
      try {
        const [bidsRes, auctionRes] = await Promise.all([
          axios.get(`${API_URL}/influencer/recent-bids`),
          axios.get(`${API_URL}/influencer/auction-stats`)
        ]);
        setRecentBids(bidsRes.data || []);
        setAuctionStats(auctionRes.data);
      } catch (e) {
        setRecentBids([]);
        setAuctionStats(null);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    setLoadingAnalytics(true);
    try {
      const [analyticsRes, audienceRes] = await Promise.all([
        axios.get(`${API_URL}/influencer/analytics?days=${analyticsPeriod}`),
        axios.get(`${API_URL}/influencer/audience`)
      ]);
      setAnalyticsData(analyticsRes.data);
      setAudienceData(audienceRes.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoadingAnalytics(false);
    }
  };

  const fetchAudience = async () => {
    try {
      const res = await axios.get(`${API_URL}/influencer/audience`);
      setAudienceData(res.data);
    } catch (error) {
      console.error('Error fetching audience data:', error);
    }
  };

  const fetchVenueIntelligence = async () => {
    setLoadingVenue(true);
    try {
      const res = await axios.get(`${API_URL}/influencer/venue-intelligence`);
      setVenueData(res.data);
    } catch (error) {
      console.error('Error fetching venue data:', error);
    } finally {
      setLoadingVenue(false);
    }
  };

  const fetchPromoCodes = async () => {
    try {
      const res = await axios.get(`${API_URL}/promo-codes`);
      setPromoCodes(res.data);
    } catch (error) {
      console.error('Error fetching promo codes:', error);
    }
  };

  const fetchSocialHandles = async () => {
    try {
      const res = await axios.get(`${API_URL}/profile/social`);
      setSocialHandles(res.data.social_handles || {});
    } catch (error) {
      console.error('Error fetching social handles:', error);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const res = await axios.get(`${API_URL}/team/members`);
      setTeamMembers(res.data);
    } catch (error) {
      console.error('Error fetching team members:', error);
    }
  };

  const fetchPayoutData = async () => {
    setLoadingPayouts(true);
    try {
      const [balanceRes, historyRes] = await Promise.all([
        axios.get(`${API_URL}/payouts/balance`),
        axios.get(`${API_URL}/payouts/history`)
      ]);
      setPayoutBalance(balanceRes.data);
      setPayoutHistory(historyRes.data.payouts || []);
    } catch (error) {
      console.error('Error fetching payout data:', error);
    } finally {
      setLoadingPayouts(false);
    }
  };

  const handleRequestPayout = async () => {
    if (!payoutRequest.amount || !payoutRequest.paypal_email) {
      toast.error('Please enter amount and PayPal email');
      return;
    }
    
    const amount = parseFloat(payoutRequest.amount);
    if (isNaN(amount) || amount < 10) {
      toast.error('Minimum payout amount is $10');
      return;
    }

    setRequestingPayout(true);
    try {
      await axios.post(`${API_URL}/payouts/request`, {
        amount: amount,
        paypal_email: payoutRequest.paypal_email,
        payout_type: payoutRequest.payout_type
      });
      toast.success('Payout request submitted! Check your PayPal email.');
      setShowPayoutModal(false);
      setPayoutRequest({ amount: '', paypal_email: '', payout_type: 'early' });
      fetchPayoutData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request payout');
    } finally {
      setRequestingPayout(false);
    }
  };

  const handleMarkEventCompleted = async (eventId) => {
    if (!window.confirm('Mark this event as completed? This will enable the final payout for this event.')) return;
    try {
      await axios.put(`${API_URL}/events/${eventId}/complete`);
      toast.success('Event marked as completed!');
      fetchDashboardData();
      fetchPayoutData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark event as completed');
    }
  };

  const handleInviteTeamMember = async () => {
    if (!newTeamMember.email) {
      toast.error('Please enter an email address');
      return;
    }
    setInvitingMember(true);
    try {
      await axios.post(`${API_URL}/team/invite`, {
        email: newTeamMember.email,
        role: newTeamMember.role,
        event_ids: newTeamMember.event_ids.length > 0 ? newTeamMember.event_ids : null
      });
      toast.success('Team member invited! They will receive an email shortly.');
      setShowTeamModal(false);
      setNewTeamMember({ email: '', role: 'scanner', event_ids: [] });
      fetchTeamMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to invite team member');
    } finally {
      setInvitingMember(false);
    }
  };

  const handleRemoveTeamMember = async (memberId) => {
    if (!window.confirm('Remove this team member?')) return;
    try {
      await axios.delete(`${API_URL}/team/members/${memberId}`);
      toast.success('Team member removed');
      fetchTeamMembers();
    } catch (error) {
      toast.error('Failed to remove team member');
    }
  };

  const handleUpdateTeamMember = async (memberId) => {
    if (!editingMember) return;
    try {
      await axios.put(`${API_URL}/team/members/${memberId}`, {
        role: editingMember.role,
        event_ids: editingMember.event_ids?.length > 0 ? editingMember.event_ids : null
      });
      toast.success('Team member updated');
      setEditingMember(null);
      fetchTeamMembers();
    } catch (error) {
      toast.error('Failed to update team member');
    }
  };

  const handleCreatePromo = async () => {
    if (!newPromo.code || !newPromo.discount_value) {
      toast.error('Please fill in code and discount value');
      return;
    }
    setCreatingPromo(true);
    try {
      await axios.post(`${API_URL}/promo-codes`, {
        code: newPromo.code,
        discount_type: newPromo.discount_type,
        discount_value: parseFloat(newPromo.discount_value),
        max_uses: newPromo.max_uses ? parseInt(newPromo.max_uses) : null,
        expires_at: newPromo.expires_at || null,
        event_id: newPromo.event_id || null
      });
      toast.success('Promo code created!');
      setShowPromoModal(false);
      setNewPromo({ code: '', discount_type: 'percentage', discount_value: 10, max_uses: '', expires_at: '', event_id: '' });
      fetchPromoCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create promo code');
    } finally {
      setCreatingPromo(false);
    }
  };

  const handleDeletePromo = async (codeId) => {
    if (!window.confirm('Delete this promo code?')) return;
    try {
      await axios.delete(`${API_URL}/promo-codes/${codeId}`);
      toast.success('Promo code deleted');
      fetchPromoCodes();
    } catch (error) {
      toast.error('Failed to delete promo code');
    }
  };

  const handleTogglePromo = async (codeId) => {
    try {
      const res = await axios.put(`${API_URL}/promo-codes/${codeId}/toggle`);
      toast.success(res.data.message);
      fetchPromoCodes();
    } catch (error) {
      toast.error('Failed to toggle promo code');
    }
  };

  const handleConnectSocial = async () => {
    if (!newSocialHandle.handle) {
      toast.error('Please enter your handle');
      return;
    }
    setConnectingSocial(true);
    try {
      await axios.post(`${API_URL}/profile/social/connect`, {
        platform: newSocialHandle.platform,
        handle: newSocialHandle.handle
      });
      toast.success(`${newSocialHandle.platform} connected!`);
      setShowSocialModal(false);
      setNewSocialHandle({ platform: 'instagram', handle: '' });
      fetchSocialHandles();
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to connect');
    } finally {
      setConnectingSocial(false);
    }
  };

  const handleDisconnectSocial = async (platform) => {
    if (!window.confirm(`Disconnect ${platform}?`)) return;
    try {
      await axios.delete(`${API_URL}/profile/social/${platform}`);
      toast.success(`${platform} disconnected`);
      fetchSocialHandles();
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error('Failed to disconnect');
    }
  };

  const handleRequestVerification = async (platform) => {
    try {
      await axios.post(`${API_URL}/profile/social/${platform}/verify`);
      toast.success('Verification requested! We\'ll review within 24-48 hours.');
      fetchSocialHandles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request verification');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    
    try {
      await axios.delete(`${API_URL}/events/${eventId}`);
      toast.success('Event deleted');
      fetchDashboardData();
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  const handlePublishEvent = async (eventId) => {
    try {
      await axios.put(`${API_URL}/events/${eventId}/publish`);
      toast.success('Event published!');
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to publish event');
    }
  };

  const handleSendBroadcast = async () => {
    if (!selectedEvent || !broadcastMessage.trim()) return;
    
    setSendingBroadcast(true);
    try {
      await axios.post(`${API_URL}/events/${selectedEvent.id}/broadcast`, {
        message: broadcastMessage
      });
      toast.success('Broadcast sent to all ticket holders!');
      setShowBroadcastModal(false);
      setBroadcastMessage('');
      setSelectedEvent(null);
    } catch (error) {
      toast.error('Failed to send broadcast');
    } finally {
      setSendingBroadcast(false);
    }
  };

  const copyProfileLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/${user?.username}`);
    toast.success('Profile link copied!');
  };

  if (authLoading || (!isAuthenticated && !authLoading) || (!isInfluencer && !authLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  const publishedEvents = events.filter(e => e.status === 'published');
  const draftEvents = events.filter(e => e.status === 'draft');
  const completedEvents = events.filter(e => e.status === 'completed' || e.status === 'cancelled');

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Email Verification Banner */}
        <EmailVerificationBanner />
        
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading font-bold text-3xl sm:text-4xl mb-2">
              Creator Dashboard
            </h1>
            <p className="text-muted-foreground">
              Welcome back, {user?.display_name}! Manage your events and connect with fans.
            </p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" className="gap-2" onClick={copyProfileLink}>
              <Copy className="w-4 h-4" />
              Share Profile
            </Button>
            <Link to="/create-event">
              <Button className="gap-2" data-testid="create-event-btn">
                <Plus className="w-5 h-5" />
                Create Event
              </Button>
            </Link>
          </div>
        </div>

        {/* Dashboard Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-card border border-white/10 p-1 flex-wrap h-auto">
            <TabsTrigger value="overview" className="gap-2" data-testid="tab-overview">
              <PieChart className="w-4 h-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="events" className="gap-2" data-testid="tab-events">
              <Calendar className="w-4 h-4" />
              Events
            </TabsTrigger>
            <TabsTrigger value="analytics" className="gap-2" data-testid="tab-analytics">
              <BarChart3 className="w-4 h-4" />
              Analytics
            </TabsTrigger>
            <TabsTrigger value="earnings" className="gap-2" data-testid="tab-earnings">
              <Wallet className="w-4 h-4" />
              Earnings
            </TabsTrigger>
            <TabsTrigger value="promos" className="gap-2" data-testid="tab-promos">
              <Tag className="w-4 h-4" />
              Promo Codes
            </TabsTrigger>
            <TabsTrigger value="social" className="gap-2" data-testid="tab-social">
              <Shield className="w-4 h-4" />
              Verification
            </TabsTrigger>
            <TabsTrigger value="team" className="gap-2" data-testid="tab-team">
              <Users className="w-4 h-4" />
              Team
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="bg-card border-white/10">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Active Events
                  </CardTitle>
                  <Calendar className="w-5 h-5 text-primary" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-heading font-bold">{publishedEvents.length}</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {draftEvents.length} drafts pending
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-card border-white/10">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Tickets Sold
                  </CardTitle>
                  <Ticket className="w-5 h-5 text-primary" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-heading font-bold">{stats.total_tickets_sold}</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    across all events
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-card border-white/10">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Net Earnings
                  </CardTitle>
                  <DollarSign className="w-5 h-5 text-green-500" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-heading font-bold text-green-500">
                    ${(stats.net_revenue || 0).toLocaleString()}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    after platform fee
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-card border-white/10">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Followers
                  </CardTitle>
                  <Users className="w-5 h-5 text-primary" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-heading font-bold">{stats.total_followers || user?.follower_count || 0}</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    on FanBound
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Quick Actions & Recent Activity */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Quick Actions */}
              <Card className="bg-card border-white/10">
                <CardHeader>
                  <CardTitle className="text-lg">Quick Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button 
                    variant="outline" 
                    className="w-full justify-start gap-3"
                    onClick={() => navigate('/create-event')}
                  >
                    <Plus className="w-5 h-5 text-primary" />
                    Create New Event
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start gap-3"
                    onClick={() => navigate('/settings')}
                  >
                    <Settings className="w-5 h-5 text-primary" />
                    Edit Profile
                  </Button>
                  {user?.username && (
                    <Button 
                      variant="outline" 
                      className="w-full justify-start gap-3"
                      onClick={() => navigate(`/${user.username}`)}
                    >
                      <Eye className="w-5 h-5 text-primary" />
                      View Public Profile
                    </Button>
                  )}
                </CardContent>
              </Card>

              {/* Recent Sales */}
              <Card className="bg-card border-white/10 lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-lg">Recent Ticket Sales</CardTitle>
                </CardHeader>
                <CardContent>
                  {recentTickets.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Ticket className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No ticket sales yet. Create an event to get started!</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {recentTickets.slice(0, 5).map((ticket, i) => (
                        <div key={i} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                          <div>
                            <p className="font-medium">{ticket.fan_name}</p>
                            <p className="text-sm text-muted-foreground">{ticket.event_title} - {ticket.tier_name}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-green-500">+${ticket.price_paid}</p>
                            <p className="text-xs text-muted-foreground">{format(new Date(ticket.purchased_at), 'MMM d')}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Recent Auction Bids */}
              <Card className="bg-card border-white/10 lg:col-span-2">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Gavel className="w-5 h-5 text-purple-400" />
                      Auction Bids
                    </CardTitle>
                    {auctionStats && auctionStats.total_winning_bids > 0 && (
                      <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30">
                        ${auctionStats.total_bid_revenue?.toLocaleString()} total
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {recentBids.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Gavel className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No auction bids yet. Create an event with auction tickets!</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {recentBids.slice(0, 5).map((bid, i) => (
                        <div key={i} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                          <div>
                            <p className="font-medium">{bid.fan_name}</p>
                            <p className="text-sm text-muted-foreground">{bid.event_title} - {bid.tier_name}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-purple-400">${bid.amount}</p>
                            <Badge variant="outline" className="text-xs border-green-500/30 text-green-400">
                              Winning
                            </Badge>
                          </div>
                        </div>
                      ))}
                      
                      {/* Auction tier summary */}
                      {auctionStats?.auction_tiers?.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-white/10">
                          <p className="text-xs text-muted-foreground mb-2">Auction Tiers</p>
                          {auctionStats.auction_tiers.map((tier, i) => (
                            <div key={i} className="flex items-center justify-between text-sm py-1">
                              <span className="text-muted-foreground">{tier.event_title} - {tier.tier_name}</span>
                              <span>
                                <span className="text-purple-400">{tier.filled_slots}/{tier.total_slots}</span>
                                <span className="text-muted-foreground"> spots</span>
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Active Events Preview */}
            {publishedEvents.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading font-semibold text-lg">Your Active Events</h3>
                  <Button variant="ghost" size="sm" onClick={() => setActiveTab('events')}>
                    View All <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {publishedEvents.slice(0, 3).map((event) => (
                    <EventCard key={event.id} event={event} />
                  ))}
                </div>
              </div>
            )}

            {/* Empty State */}
            {events.length === 0 && !loading && (
              <Card className="bg-card border-white/10">
                <CardContent className="text-center py-16">
                  <Calendar className="w-20 h-20 text-muted-foreground mx-auto mb-6" />
                  <h3 className="font-heading font-bold text-2xl mb-3">Create Your First Event</h3>
                  <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                    Start connecting with your fans through exclusive meetups, workshops, dinners, and more. 
                    Set your price, pick a date range, and let us help you find the perfect venue based on demand.
                  </p>
                  <Link to="/create-event">
                    <Button size="lg" className="gap-2">
                      <Plus className="w-5 h-5" />
                      Create Your First Event
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Events Tab */}
          <TabsContent value="events" className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="font-heading font-semibold text-xl">All Events</h2>
              <Link to="/create-event">
                <Button className="gap-2">
                  <Plus className="w-4 h-4" />
                  New Event
                </Button>
              </Link>
            </div>

            {/* Draft Events */}
            {draftEvents.length > 0 && (
              <div>
                <h3 className="font-medium text-muted-foreground mb-3 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Drafts ({draftEvents.length})
                </h3>
                <div className="space-y-3">
                  {draftEvents.map((event) => (
                    <Card key={event.id} className="bg-card border-white/10 border-l-4 border-l-yellow-500">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-16 h-16 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                              <Calendar className="w-8 h-8 text-yellow-500" />
                            </div>
                            <div>
                              <h4 className="font-heading font-semibold">{event.title}</h4>
                              <p className="text-sm text-muted-foreground">
                                {event.category} • {event.location || 'Location TBD'}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" onClick={() => navigate(`/events/${event.id}/edit`)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button size="sm" onClick={() => handlePublishEvent(event.id)}>
                              Publish
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteEvent(event.id)}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Published Events */}
            {publishedEvents.length > 0 && (
              <div>
                <h3 className="font-medium text-muted-foreground mb-3 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Active ({publishedEvents.length})
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {publishedEvents.map((event) => (
                    <Card key={event.id} className="bg-card border-white/10 hover:border-primary/30 transition-colors">
                      <CardContent className="p-4">
                        <div className="flex gap-4">
                          <div className="w-24 h-24 rounded-lg bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center flex-shrink-0 overflow-hidden">
                            <img 
                              src={event.image_url || 'https://customer-assets.emergentagent.com/job_creator-signup-4/artifacts/7bii36vj_ChatGPT%20Image%20Mar%2031%2C%202026%2C%2008_44_35%20PM.png'} 
                              alt="" 
                              className="w-full h-full object-cover rounded-lg" 
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between">
                              <div>
                                <h4 className="font-heading font-semibold truncate">{event.title}</h4>
                                <p className="text-sm text-muted-foreground">
                                  {event.category} • {event.event_type}
                                </p>
                              </div>
                              <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Live</Badge>
                            </div>
                            <div className="flex items-center gap-4 mt-2 text-sm">
                              <span className="flex items-center gap-1">
                                <Ticket className="w-4 h-4" />
                                {event.sold_tickets}/{event.total_tickets}
                              </span>
                              <span className="flex items-center gap-1">
                                <MapPin className="w-4 h-4" />
                                {event.location || 'TBD'}
                              </span>
                            </div>
                            <div className="flex gap-2 mt-3">
                              <Button size="sm" variant="outline" onClick={() => navigate(`/events/${event.id}`)}>
                                View
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => navigate(`/events/${event.id}/edit`)}>
                                <Edit className="w-4 h-4 mr-1" />
                                Edit
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => {
                                  setSelectedEvent(event);
                                  setShowBroadcastModal(true);
                                }}
                              >
                                <Megaphone className="w-4 h-4 mr-1" />
                                Broadcast
                              </Button>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Completed Events */}
            {completedEvents.length > 0 && (
              <div>
                <h3 className="font-medium text-muted-foreground mb-3 flex items-center gap-2">
                  <XCircle className="w-4 h-4" />
                  Past Events ({completedEvents.length})
                </h3>
                <div className="space-y-2">
                  {completedEvents.map((event) => (
                    <Card key={event.id} className="bg-card border-white/10 opacity-70">
                      <CardContent className="p-4 flex items-center justify-between">
                        <div>
                          <h4 className="font-medium">{event.title}</h4>
                          <p className="text-sm text-muted-foreground">
                            {event.sold_tickets} tickets sold • ${event.total_revenue || 0} revenue
                          </p>
                        </div>
                        <Badge variant="secondary">{event.status}</Badge>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {events.length === 0 && (
              <Card className="bg-card border-white/10">
                <CardContent className="text-center py-12">
                  <Calendar className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-heading font-semibold text-xl mb-2">No events yet</h3>
                  <p className="text-muted-foreground mb-6">
                    Create your first event to start selling tickets.
                  </p>
                  <Link to="/create-event">
                    <Button className="gap-2">
                      <Plus className="w-5 h-5" />
                      Create Event
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="space-y-6">
            {/* Time Period Filter */}
            <div className="flex items-center justify-between">
              <h2 className="font-heading font-semibold text-xl">Analytics Dashboard</h2>
              <Select value={analyticsPeriod} onValueChange={setAnalyticsPeriod}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                  <SelectItem value="365">Last year</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {loadingAnalytics ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : (
              <>
                {/* Quick Stats */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card className="bg-card border-white/10">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-muted-foreground">Total Events</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-heading font-bold">{stats.total_events}</div>
                    </CardContent>
                  </Card>
                  <Card className="bg-card border-white/10">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-muted-foreground">Total Tickets Sold</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-heading font-bold">{stats.total_tickets_sold}</div>
                    </CardContent>
                  </Card>
                  <Card className="bg-card border-white/10">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-muted-foreground">Gross Revenue</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-heading font-bold">${(stats.total_revenue || 0).toLocaleString()}</div>
                    </CardContent>
                  </Card>
                  <Card className="bg-card border-white/10">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-muted-foreground">Net Payout</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-heading font-bold text-green-500">${(stats.net_revenue || 0).toLocaleString()}</div>
                    </CardContent>
                  </Card>
                </div>

                {/* Sales Chart */}
                <Card className="bg-card border-white/10">
                  <CardHeader>
                    <CardTitle>Revenue Trend</CardTitle>
                    <CardDescription>Daily sales over the selected period</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {analyticsData?.daily_sales?.length > 0 ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <AreaChart data={analyticsData.daily_sales}>
                          <defs>
                            <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3}/>
                              <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                          <XAxis 
                            dataKey="date" 
                            stroke="#888" 
                            fontSize={12}
                            tickFormatter={(value) => format(new Date(value), 'MMM d')}
                          />
                          <YAxis stroke="#888" fontSize={12} tickFormatter={(value) => `$${value}`} />
                          <Tooltip 
                            contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #333' }}
                            formatter={(value) => [`$${value}`, 'Revenue']}
                            labelFormatter={(label) => format(new Date(label), 'MMM d, yyyy')}
                          />
                          <Area type="monotone" dataKey="revenue" stroke="#8b5cf6" fillOpacity={1} fill="url(#colorRevenue)" />
                        </AreaChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="text-center py-12 text-muted-foreground">
                        <LineChart className="w-16 h-16 mx-auto mb-4 opacity-50" />
                        <p>No sales data for this period yet.</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Top Events */}
                  <Card className="bg-card border-white/10">
                    <CardHeader>
                      <CardTitle>Top Performing Events</CardTitle>
                      <CardDescription>Ranked by revenue</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {analyticsData?.event_performance?.length > 0 ? (
                        <div className="space-y-3">
                          {analyticsData.event_performance.slice(0, 5).map((event, i) => (
                            <div key={event.event_id} className="flex items-center gap-3 p-2 bg-secondary/30 rounded-lg">
                              <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-sm font-bold">
                                {i + 1}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium truncate">{event.title}</p>
                                <p className="text-xs text-muted-foreground">
                                  {event.tickets_sold} sold • {event.conversion_rate}% conversion
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="font-semibold text-green-500">${event.revenue}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-muted-foreground">
                          No event data available yet.
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Sales by Tier */}
                  <Card className="bg-card border-white/10">
                    <CardHeader>
                      <CardTitle>Sales by Ticket Tier</CardTitle>
                      <CardDescription>Revenue breakdown by tier type</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {analyticsData?.sales_by_tier?.length > 0 ? (
                        <ResponsiveContainer width="100%" height={200}>
                          <BarChart data={analyticsData.sales_by_tier} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                            <XAxis type="number" stroke="#888" tickFormatter={(value) => `$${value}`} />
                            <YAxis dataKey="tier" type="category" stroke="#888" width={100} fontSize={12} />
                            <Tooltip 
                              contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #333' }}
                              formatter={(value) => [`$${value}`, 'Revenue']}
                            />
                            <Bar dataKey="revenue" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="text-center py-8 text-muted-foreground">
                          No tier data available yet.
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Audience Insights */}
                <Card className="bg-card border-white/10">
                  <CardHeader>
                    <CardTitle>Audience Insights</CardTitle>
                    <CardDescription>Demographics of your ticket buyers</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {audienceData && audienceData.total_unique_fans > 0 ? (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="text-center p-4 bg-secondary/30 rounded-lg">
                          <div className="text-3xl font-bold text-primary">{audienceData.total_unique_fans}</div>
                          <div className="text-sm text-muted-foreground">Unique Fans</div>
                        </div>
                        <div className="text-center p-4 bg-secondary/30 rounded-lg">
                          <div className="text-3xl font-bold text-cyan-400">{audienceData.new_fans_30d}</div>
                          <div className="text-sm text-muted-foreground">New (30d)</div>
                        </div>
                        <div className="text-center p-4 bg-secondary/30 rounded-lg">
                          <div className="text-3xl font-bold text-green-400">{audienceData.repeat_buyers}</div>
                          <div className="text-sm text-muted-foreground">Repeat Buyers</div>
                        </div>
                        <div className="text-center p-4 bg-secondary/30 rounded-lg">
                          <div className="text-3xl font-bold text-amber-400">{audienceData.avg_tickets_per_fan}</div>
                          <div className="text-sm text-muted-foreground">Avg Tickets/Fan</div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-12 text-muted-foreground">
                        <Users className="w-16 h-16 mx-auto mb-4 opacity-50" />
                        <p>Detailed audience demographics will be available after more ticket sales.</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Venue Intelligence */}
                <Card className="bg-card border-white/10">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Building className="w-5 h-5" />
                        Venue Intelligence
                      </CardTitle>
                      <CardDescription>Suggested locations based on your audience</CardDescription>
                    </div>
                    <Button variant="outline" size="sm" onClick={fetchVenueIntelligence} disabled={loadingVenue}>
                      <RefreshCw className={`w-4 h-4 mr-2 ${loadingVenue ? 'animate-spin' : ''}`} />
                      Refresh
                    </Button>
                  </CardHeader>
                  <CardContent>
                    {loadingVenue ? (
                      <div className="flex items-center justify-center py-8">
                        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
                      </div>
                    ) : venueData?.suggested_cities?.length > 0 ? (
                      <div className="space-y-4">
                        <div className="p-4 bg-primary/10 border border-primary/30 rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <Target className="w-5 h-5 text-primary" />
                            <span className="font-semibold">Recommendation</span>
                          </div>
                          <p className="text-sm text-muted-foreground">{venueData.insights?.recommendation}</p>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                          {venueData.suggested_cities.map((city, i) => (
                            <div key={city.city} className="p-3 bg-secondary/30 rounded-lg">
                              <div className="flex items-center gap-2 mb-1">
                                <MapPin className="w-4 h-4 text-primary" />
                                <span className="font-medium">{city.city}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <div className="flex-1 h-2 bg-secondary rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-primary transition-all" 
                                    style={{ width: `${city.demand_score}%` }}
                                  />
                                </div>
                                <span className="text-xs text-muted-foreground">{city.demand_score}%</span>
                              </div>
                              <p className="text-xs text-muted-foreground mt-1">{city.recommendation}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <Building className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>Create events with locations to unlock venue intelligence.</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          {/* Earnings Tab */}
          <TabsContent value="earnings" className="space-y-6">
            {/* Balance Overview */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="bg-card border-white/10">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-muted-foreground">Total Earned</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-heading font-bold text-green-500">
                    ${(payoutBalance?.total_earnings || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Your 70% share (after 30% fee)</p>
                </CardContent>
              </Card>
              <Card className="bg-card border-white/10">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-muted-foreground">Already Paid Out</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-heading font-bold text-blue-400">
                    ${(payoutBalance?.total_paid_out || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Total received</p>
                </CardContent>
              </Card>
              <Card className="bg-card border-white/10">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-muted-foreground">Available Balance</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-heading font-bold">
                    ${(payoutBalance?.remaining_balance || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Remaining to withdraw</p>
                </CardContent>
              </Card>
              <Card className="bg-card border-white/10">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-muted-foreground">Tickets Sold</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-heading font-bold">
                    {payoutBalance?.total_tickets_sold || 0}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Total sold</p>
                </CardContent>
              </Card>
            </div>

            {/* Payout Options */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="bg-gradient-to-br from-purple-900/30 to-purple-800/10 border-purple-700/30">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Banknote className="w-5 h-5 text-purple-400" />
                    Early Payout (Pre-Event)
                  </CardTitle>
                  <CardDescription>
                    Request up to 50% of your earnings before your event
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Available for early payout:</span>
                    <span className="text-2xl font-bold text-purple-400">
                      ${(payoutBalance?.early_payout?.available || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                    </span>
                  </div>
                  <div className="text-sm text-muted-foreground space-y-1">
                    <p>Max early payout (50% of total): ${(payoutBalance?.early_payout?.max_allowed || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
                    <p>Already paid early: ${(payoutBalance?.early_payout?.already_paid || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
                  </div>
                  <Button 
                    className="w-full bg-purple-600 hover:bg-purple-700"
                    onClick={() => {
                      setPayoutRequest({ ...payoutRequest, payout_type: 'early' });
                      setShowPayoutModal(true);
                    }}
                    disabled={(payoutBalance?.early_payout?.available || 0) < 10}
                  >
                    <Banknote className="w-4 h-4 mr-2" />
                    Request Early Payout
                  </Button>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-green-900/30 to-green-800/10 border-green-700/30">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    Final Payout (Post-Event)
                  </CardTitle>
                  <CardDescription>
                    Remaining 50% available after your event is completed
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Available for final payout:</span>
                    <span className="text-2xl font-bold text-green-400">
                      ${(payoutBalance?.final_payout?.available || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                    </span>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    <p>Completed events: {payoutBalance?.final_payout?.completed_events || 0}</p>
                    {(payoutBalance?.final_payout?.completed_events || 0) === 0 && (
                      <p className="text-yellow-500 mt-2">Mark events as completed to unlock final payouts</p>
                    )}
                  </div>
                  <Button 
                    className="w-full bg-green-600 hover:bg-green-700"
                    onClick={() => {
                      setPayoutRequest({ ...payoutRequest, payout_type: 'final' });
                      setShowPayoutModal(true);
                    }}
                    disabled={(payoutBalance?.final_payout?.available || 0) < 10}
                  >
                    <Banknote className="w-4 h-4 mr-2" />
                    Request Final Payout
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Mark Events as Completed */}
            {events.filter(e => e.status === 'published').length > 0 && (
              <Card className="bg-card border-white/10">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    Mark Events as Completed
                  </CardTitle>
                  <CardDescription>
                    After your event has finished, mark it as completed to unlock the final 50% payout
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {events.filter(e => e.status === 'published').map(event => (
                      <div key={event.id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                        <div>
                          <p className="font-medium">{event.title}</p>
                          <p className="text-sm text-muted-foreground">
                            {event.event_date || event.flexible_date || 'Date TBD'} • {event.location || 'Location TBD'}
                          </p>
                        </div>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleMarkEventCompleted(event.id)}
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Mark Completed
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Payout History */}
            <Card className="bg-card border-white/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <History className="w-5 h-5" />
                  Payout History
                </CardTitle>
                <CardDescription>Your payout requests and their status</CardDescription>
              </CardHeader>
              <CardContent>
                {loadingPayouts ? (
                  <div className="text-center py-8">
                    <RefreshCw className="w-8 h-8 mx-auto mb-2 animate-spin text-muted-foreground" />
                    <p className="text-muted-foreground">Loading...</p>
                  </div>
                ) : payoutHistory.length > 0 ? (
                  <div className="space-y-3">
                    {payoutHistory.map(payout => (
                      <div key={payout.id} className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                        <div className="flex items-center gap-4">
                          <div className={`p-2 rounded-full ${
                            payout.status === 'completed' ? 'bg-green-500/20' :
                            payout.status === 'processing' ? 'bg-yellow-500/20' :
                            payout.status === 'failed' ? 'bg-red-500/20' : 'bg-gray-500/20'
                          }`}>
                            {payout.status === 'completed' ? <CheckCircle className="w-5 h-5 text-green-500" /> :
                             payout.status === 'processing' ? <Clock className="w-5 h-5 text-yellow-500" /> :
                             payout.status === 'failed' ? <XCircle className="w-5 h-5 text-red-500" /> :
                             <Clock className="w-5 h-5 text-gray-500" />}
                          </div>
                          <div>
                            <p className="font-medium">${payout.amount.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
                            <p className="text-sm text-muted-foreground">
                              {payout.payout_type === 'early' ? 'Early Payout' : 'Final Payout'} • {payout.paypal_email}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(payout.created_at).toLocaleDateString('en-US', { 
                                month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
                              })}
                            </p>
                          </div>
                        </div>
                        <Badge variant={
                          payout.status === 'completed' ? 'default' :
                          payout.status === 'processing' ? 'secondary' :
                          payout.status === 'failed' ? 'destructive' : 'outline'
                        } className={
                          payout.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                          payout.status === 'processing' ? 'bg-yellow-500/20 text-yellow-400' : ''
                        }>
                          {payout.status.charAt(0).toUpperCase() + payout.status.slice(1)}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <Wallet className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p className="mb-2">No payouts yet</p>
                    <p className="text-sm">Request your first payout above when you have earnings!</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Revenue Breakdown */}
            <Card className="bg-card border-white/10">
              <CardHeader>
                <CardTitle>Revenue Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-b border-white/5">
                    <span className="text-muted-foreground">Gross Ticket Sales</span>
                    <span className="font-semibold">${(stats.total_revenue || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-white/5">
                    <span className="text-muted-foreground">Platform Fee (30%)</span>
                    <span className="font-semibold text-red-400">
                      -${((stats.total_revenue || 0) * 0.3).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 font-semibold text-lg">
                    <span>Your Earnings (70%)</span>
                    <span className="text-green-500">${(payoutBalance?.total_earnings || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Promo Codes Tab */}
          <TabsContent value="promos" className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-heading font-semibold text-xl">Promo Codes</h2>
                <p className="text-sm text-muted-foreground">Create discount codes for your events</p>
              </div>
              <Button className="gap-2" onClick={() => setShowPromoModal(true)} data-testid="create-promo-btn">
                <Plus className="w-4 h-4" />
                Create Code
              </Button>
            </div>

            {promoCodes.length === 0 ? (
              <Card className="bg-card border-white/10">
                <CardContent className="text-center py-12">
                  <Tag className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-heading font-semibold text-xl mb-2">No promo codes yet</h3>
                  <p className="text-muted-foreground mb-6">
                    Create discount codes to offer special deals to your fans.
                  </p>
                  <Button className="gap-2" onClick={() => setShowPromoModal(true)}>
                    <Plus className="w-5 h-5" />
                    Create Your First Code
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {promoCodes.map((promo) => (
                  <Card key={promo.id} className={`bg-card border-white/10 ${!promo.is_active ? 'opacity-60' : ''}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center">
                            {promo.discount_type === 'percentage' ? (
                              <Percent className="w-6 h-6 text-primary" />
                            ) : (
                              <DollarSign className="w-6 h-6 text-primary" />
                            )}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-mono font-bold text-lg">{promo.code}</span>
                              {!promo.is_active && <Badge variant="secondary">Inactive</Badge>}
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {promo.discount_type === 'percentage' 
                                ? `${promo.discount_value}% off` 
                                : `$${promo.discount_value} off`}
                              {promo.max_uses && ` • ${promo.uses || 0}/${promo.max_uses} used`}
                              {promo.expires_at && ` • Expires ${format(new Date(promo.expires_at), 'MMM d, yyyy')}`}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => {
                              navigator.clipboard.writeText(promo.code);
                              toast.success('Code copied!');
                            }}
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleTogglePromo(promo.id)}
                          >
                            {promo.is_active ? 'Disable' : 'Enable'}
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleDeletePromo(promo.id)}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Social Verification Tab */}
          <TabsContent value="social" className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-heading font-semibold text-xl">Social Verification</h2>
                <p className="text-sm text-muted-foreground">Connect and verify your social media accounts</p>
              </div>
              <Button className="gap-2" onClick={() => setShowSocialModal(true)} data-testid="connect-social-btn">
                <Plus className="w-4 h-4" />
                Connect Account
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Instagram */}
              <Card className={`bg-card border-white/10 ${socialHandles.instagram ? 'border-l-4 border-l-pink-500' : ''}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                        <Instagram className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-medium">Instagram</p>
                        {socialHandles.instagram ? (
                          <p className="text-sm text-muted-foreground">@{socialHandles.instagram.handle}</p>
                        ) : (
                          <p className="text-sm text-muted-foreground">Not connected</p>
                        )}
                      </div>
                    </div>
                    {socialHandles.instagram ? (
                      <div className="flex items-center gap-2">
                        {socialHandles.instagram.verified ? (
                          <Badge className="bg-green-500/20 text-green-400">Verified</Badge>
                        ) : socialHandles.instagram.verification_requested ? (
                          <Badge className="bg-yellow-500/20 text-yellow-400">Pending</Badge>
                        ) : (
                          <Button size="sm" variant="outline" onClick={() => handleRequestVerification('instagram')}>
                            Request Verify
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => handleDisconnectSocial('instagram')}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => {
                        setNewSocialHandle({ platform: 'instagram', handle: '' });
                        setShowSocialModal(true);
                      }}>
                        Connect
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* TikTok */}
              <Card className={`bg-card border-white/10 ${socialHandles.tiktok ? 'border-l-4 border-l-black' : ''}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-black flex items-center justify-center">
                        <TikTokIcon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-medium">TikTok</p>
                        {socialHandles.tiktok ? (
                          <p className="text-sm text-muted-foreground">@{socialHandles.tiktok.handle}</p>
                        ) : (
                          <p className="text-sm text-muted-foreground">Not connected</p>
                        )}
                      </div>
                    </div>
                    {socialHandles.tiktok ? (
                      <div className="flex items-center gap-2">
                        {socialHandles.tiktok.verified ? (
                          <Badge className="bg-green-500/20 text-green-400">Verified</Badge>
                        ) : socialHandles.tiktok.verification_requested ? (
                          <Badge className="bg-yellow-500/20 text-yellow-400">Pending</Badge>
                        ) : (
                          <Button size="sm" variant="outline" onClick={() => handleRequestVerification('tiktok')}>
                            Request Verify
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => handleDisconnectSocial('tiktok')}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => {
                        setNewSocialHandle({ platform: 'tiktok', handle: '' });
                        setShowSocialModal(true);
                      }}>
                        Connect
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* YouTube */}
              <Card className={`bg-card border-white/10 ${socialHandles.youtube ? 'border-l-4 border-l-red-500' : ''}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-red-600 flex items-center justify-center">
                        <Youtube className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-medium">YouTube</p>
                        {socialHandles.youtube ? (
                          <p className="text-sm text-muted-foreground">@{socialHandles.youtube.handle}</p>
                        ) : (
                          <p className="text-sm text-muted-foreground">Not connected</p>
                        )}
                      </div>
                    </div>
                    {socialHandles.youtube ? (
                      <div className="flex items-center gap-2">
                        {socialHandles.youtube.verified ? (
                          <Badge className="bg-green-500/20 text-green-400">Verified</Badge>
                        ) : socialHandles.youtube.verification_requested ? (
                          <Badge className="bg-yellow-500/20 text-yellow-400">Pending</Badge>
                        ) : (
                          <Button size="sm" variant="outline" onClick={() => handleRequestVerification('youtube')}>
                            Request Verify
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => handleDisconnectSocial('youtube')}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => {
                        setNewSocialHandle({ platform: 'youtube', handle: '' });
                        setShowSocialModal(true);
                      }}>
                        Connect
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Twitter/X */}
              <Card className={`bg-card border-white/10 ${socialHandles.twitter ? 'border-l-4 border-l-sky-500' : ''}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-black flex items-center justify-center">
                        <Twitter className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-medium">Twitter / X</p>
                        {socialHandles.twitter ? (
                          <p className="text-sm text-muted-foreground">@{socialHandles.twitter.handle}</p>
                        ) : (
                          <p className="text-sm text-muted-foreground">Not connected</p>
                        )}
                      </div>
                    </div>
                    {socialHandles.twitter ? (
                      <div className="flex items-center gap-2">
                        {socialHandles.twitter.verified ? (
                          <Badge className="bg-green-500/20 text-green-400">Verified</Badge>
                        ) : socialHandles.twitter.verification_requested ? (
                          <Badge className="bg-yellow-500/20 text-yellow-400">Pending</Badge>
                        ) : (
                          <Button size="sm" variant="outline" onClick={() => handleRequestVerification('twitter')}>
                            Request Verify
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => handleDisconnectSocial('twitter')}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => {
                        setNewSocialHandle({ platform: 'twitter', handle: '' });
                        setShowSocialModal(true);
                      }}>
                        Connect
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card className="bg-card border-white/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-primary" />
                  Verification Benefits
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                    <div>
                      <p className="font-medium">Trust Badge</p>
                      <p className="text-sm text-muted-foreground">Verified badge displayed on your profile</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                    <div>
                      <p className="font-medium">Priority Support</p>
                      <p className="text-sm text-muted-foreground">Get faster response from our team</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                    <div>
                      <p className="font-medium">Featured Placement</p>
                      <p className="text-sm text-muted-foreground">Boosted visibility in search results</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Team Tab */}
          <TabsContent value="team" className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-heading font-semibold text-xl">Team Management</h2>
                <p className="text-sm text-muted-foreground">Invite team members to help manage events and scan tickets</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" className="gap-2" onClick={() => navigate('/scanner')} data-testid="open-scanner-btn">
                  <QrCode className="w-4 h-4" />
                  Open Scanner
                </Button>
                <Button className="gap-2" onClick={() => setShowTeamModal(true)} data-testid="invite-team-btn">
                  <UserPlus className="w-4 h-4" />
                  Invite Member
                </Button>
              </div>
            </div>

            {/* Team Roles Explanation */}
            <Card className="bg-card border-white/10">
              <CardHeader>
                <CardTitle className="text-base">Team Roles</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-3 bg-secondary/30 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <QrCode className="w-4 h-4 text-primary" />
                      <span className="font-medium">Scanner</span>
                    </div>
                    <p className="text-sm text-muted-foreground">Can scan tickets at events for check-in</p>
                  </div>
                  <div className="p-3 bg-secondary/30 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <BarChart3 className="w-4 h-4 text-cyan-400" />
                      <span className="font-medium">Manager</span>
                    </div>
                    <p className="text-sm text-muted-foreground">Can scan tickets + view event stats</p>
                  </div>
                  <div className="p-3 bg-secondary/30 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Shield className="w-4 h-4 text-amber-400" />
                      <span className="font-medium">Admin</span>
                    </div>
                    <p className="text-sm text-muted-foreground">Full access to manage events</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Team Members List */}
            {teamMembers.length === 0 ? (
              <Card className="bg-card border-white/10">
                <CardContent className="text-center py-12">
                  <Users className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-heading font-semibold text-xl mb-2">No team members yet</h3>
                  <p className="text-muted-foreground mb-6">
                    Invite team members to help with ticket scanning and event management.
                  </p>
                  <Button className="gap-2" onClick={() => setShowTeamModal(true)}>
                    <UserPlus className="w-5 h-5" />
                    Invite Your First Team Member
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {teamMembers.map((member) => (
                  <Card key={member.id} className={`bg-card border-white/10 ${member.status === 'pending' ? 'opacity-70' : ''}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                            <Mail className="w-6 h-6 text-primary" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{member.display_name || member.email}</span>
                              <Badge className={
                                member.status === 'active' ? 'bg-green-500/20 text-green-400' :
                                member.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                                'bg-gray-500/20 text-gray-400'
                              }>
                                {member.status}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">{member.email}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline" className="text-xs">
                                {member.role === 'scanner' && <QrCode className="w-3 h-3 mr-1" />}
                                {member.role === 'manager' && <BarChart3 className="w-3 h-3 mr-1" />}
                                {member.role === 'admin' && <Shield className="w-3 h-3 mr-1" />}
                                {member.role}
                              </Badge>
                              {member.event_ids ? (
                                <span className="text-xs text-muted-foreground">
                                  {member.event_ids.length} event{member.event_ids.length !== 1 ? 's' : ''}
                                </span>
                              ) : (
                                <span className="text-xs text-muted-foreground">All events</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {editingMember?.id === member.id ? (
                            <>
                              <Select 
                                value={editingMember.role} 
                                onValueChange={(v) => setEditingMember({ ...editingMember, role: v })}
                              >
                                <SelectTrigger className="w-28">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="scanner">Scanner</SelectItem>
                                  <SelectItem value="manager">Manager</SelectItem>
                                  <SelectItem value="admin">Admin</SelectItem>
                                </SelectContent>
                              </Select>
                              <Button size="sm" onClick={() => handleUpdateTeamMember(member.id)}>
                                Save
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => setEditingMember(null)}>
                                Cancel
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => setEditingMember({ 
                                  id: member.id, 
                                  role: member.role, 
                                  event_ids: member.event_ids || [] 
                                })}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleRemoveTeamMember(member.id)}
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Broadcast Modal */}
      <Dialog open={showBroadcastModal} onOpenChange={setShowBroadcastModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Broadcast</DialogTitle>
            <DialogDescription>
              Send a message to all ticket holders for "{selectedEvent?.title}"
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Textarea
              placeholder="Write your message to attendees..."
              value={broadcastMessage}
              onChange={(e) => setBroadcastMessage(e.target.value)}
              rows={5}
            />
            <Button 
              className="w-full gap-2" 
              onClick={handleSendBroadcast}
              disabled={!broadcastMessage.trim() || sendingBroadcast}
            >
              {sendingBroadcast ? 'Sending...' : (
                <>
                  <Send className="w-4 h-4" />
                  Send to All Attendees
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Promo Code Modal */}
      <Dialog open={showPromoModal} onOpenChange={setShowPromoModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Promo Code</DialogTitle>
            <DialogDescription>
              Create a discount code for your events
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Code</Label>
              <Input
                placeholder="SUMMER20"
                value={newPromo.code}
                onChange={(e) => setNewPromo({ ...newPromo, code: e.target.value.toUpperCase() })}
                className="mt-1 font-mono"
                data-testid="promo-code-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Discount Type</Label>
                <Select value={newPromo.discount_type} onValueChange={(v) => setNewPromo({ ...newPromo, discount_type: v })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage (%)</SelectItem>
                    <SelectItem value="fixed">Fixed Amount ($)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Discount Value</Label>
                <Input
                  type="number"
                  placeholder={newPromo.discount_type === 'percentage' ? '10' : '5'}
                  value={newPromo.discount_value}
                  onChange={(e) => setNewPromo({ ...newPromo, discount_value: e.target.value })}
                  className="mt-1"
                  data-testid="promo-value-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Max Uses (optional)</Label>
                <Input
                  type="number"
                  placeholder="Unlimited"
                  value={newPromo.max_uses}
                  onChange={(e) => setNewPromo({ ...newPromo, max_uses: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Expires (optional)</Label>
                <Input
                  type="date"
                  value={newPromo.expires_at}
                  onChange={(e) => setNewPromo({ ...newPromo, expires_at: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>
            <div>
              <Label>Apply to Event (optional)</Label>
              <Select value={newPromo.event_id || "all"} onValueChange={(v) => setNewPromo({ ...newPromo, event_id: v === "all" ? "" : v })}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="All events" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All events</SelectItem>
                  {events.filter(e => e.status === 'published').map(event => (
                    <SelectItem key={event.id} value={event.id}>{event.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button 
              className="w-full gap-2" 
              onClick={handleCreatePromo}
              disabled={creatingPromo}
              data-testid="create-promo-submit"
            >
              {creatingPromo ? 'Creating...' : (
                <>
                  <Tag className="w-4 h-4" />
                  Create Promo Code
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Social Connect Modal */}
      <Dialog open={showSocialModal} onOpenChange={setShowSocialModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Connect Social Account</DialogTitle>
            <DialogDescription>
              Enter your social media handle to connect it to your profile
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Platform</Label>
              <Select value={newSocialHandle.platform} onValueChange={(v) => setNewSocialHandle({ ...newSocialHandle, platform: v })}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="instagram">Instagram</SelectItem>
                  <SelectItem value="tiktok">TikTok</SelectItem>
                  <SelectItem value="youtube">YouTube</SelectItem>
                  <SelectItem value="twitter">Twitter / X</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Handle / Username</Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">@</span>
                <Input
                  placeholder="yourhandle"
                  value={newSocialHandle.handle}
                  onChange={(e) => setNewSocialHandle({ ...newSocialHandle, handle: e.target.value.replace('@', '') })}
                  className="pl-8"
                  data-testid="social-handle-input"
                />
              </div>
            </div>
            <Button 
              className="w-full gap-2" 
              onClick={handleConnectSocial}
              disabled={connectingSocial}
              data-testid="connect-social-submit"
            >
              {connectingSocial ? 'Connecting...' : (
                <>
                  <Sparkles className="w-4 h-4" />
                  Connect Account
                </>
              )}
            </Button>
            <p className="text-xs text-center text-muted-foreground">
              After connecting, you can request verification. Our team will review within 24-48 hours.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Team Invite Modal */}
      <Dialog open={showTeamModal} onOpenChange={setShowTeamModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite Team Member</DialogTitle>
            <DialogDescription>
              Send an invitation to help manage your events
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Email Address</Label>
              <Input
                type="email"
                placeholder="teammate@example.com"
                value={newTeamMember.email}
                onChange={(e) => setNewTeamMember({ ...newTeamMember, email: e.target.value })}
                className="mt-1"
                data-testid="team-email-input"
              />
            </div>
            <div>
              <Label>Role</Label>
              <Select 
                value={newTeamMember.role} 
                onValueChange={(v) => setNewTeamMember({ ...newTeamMember, role: v })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="scanner">
                    <div className="flex items-center gap-2">
                      <QrCode className="w-4 h-4" />
                      Scanner - Can scan tickets at events
                    </div>
                  </SelectItem>
                  <SelectItem value="manager">
                    <div className="flex items-center gap-2">
                      <BarChart3 className="w-4 h-4" />
                      Manager - Scan tickets + view stats
                    </div>
                  </SelectItem>
                  <SelectItem value="admin">
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      Admin - Full event access
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Event Access</Label>
              <p className="text-xs text-muted-foreground mb-2">Select which events this member can access (leave empty for all events)</p>
              <div className="max-h-40 overflow-y-auto space-y-2 p-2 bg-secondary/30 rounded-lg">
                {events.filter(e => e.status === 'published').length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-2">No published events yet</p>
                ) : (
                  events.filter(e => e.status === 'published').map(event => (
                    <label key={event.id} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={newTeamMember.event_ids.includes(event.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setNewTeamMember({
                              ...newTeamMember,
                              event_ids: [...newTeamMember.event_ids, event.id]
                            });
                          } else {
                            setNewTeamMember({
                              ...newTeamMember,
                              event_ids: newTeamMember.event_ids.filter(id => id !== event.id)
                            });
                          }
                        }}
                      />
                      <span className="text-sm">{event.title}</span>
                    </label>
                  ))
                )}
              </div>
            </div>
            <Button 
              className="w-full gap-2" 
              onClick={handleInviteTeamMember}
              disabled={invitingMember || !newTeamMember.email}
              data-testid="invite-team-submit"
            >
              {invitingMember ? 'Sending Invitation...' : (
                <>
                  <Mail className="w-4 h-4" />
                  Send Invitation
                </>
              )}
            </Button>
            <p className="text-xs text-center text-muted-foreground">
              An email will be sent with instructions to join your team.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payout Request Modal */}
      <Dialog open={showPayoutModal} onOpenChange={setShowPayoutModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Banknote className="w-5 h-5" />
              Request {payoutRequest.payout_type === 'early' ? 'Early' : 'Final'} Payout
            </DialogTitle>
            <DialogDescription>
              {payoutRequest.payout_type === 'early' 
                ? 'Request up to 50% of your earnings before your event'
                : 'Withdraw remaining earnings from completed events'
              }
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-4 bg-white/5 rounded-lg">
              <p className="text-sm text-muted-foreground mb-1">Available for {payoutRequest.payout_type} payout:</p>
              <p className="text-2xl font-bold text-green-400">
                ${payoutRequest.payout_type === 'early' 
                  ? (payoutBalance?.early_payout?.available || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})
                  : (payoutBalance?.final_payout?.available || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})
                }
              </p>
            </div>
            <div className="space-y-2">
              <Label>Payout Amount (USD)</Label>
              <Input
                type="number"
                min="10"
                step="0.01"
                placeholder="Enter amount (min $10)"
                value={payoutRequest.amount}
                onChange={(e) => setPayoutRequest({ ...payoutRequest, amount: e.target.value })}
                data-testid="payout-amount"
              />
            </div>
            <div className="space-y-2">
              <Label>PayPal Email</Label>
              <Input
                type="email"
                placeholder="your-paypal@email.com"
                value={payoutRequest.paypal_email}
                onChange={(e) => setPayoutRequest({ ...payoutRequest, paypal_email: e.target.value })}
                data-testid="payout-paypal-email"
              />
              <p className="text-xs text-muted-foreground">
                Enter the email associated with your PayPal account
              </p>
            </div>
            <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
              <p className="text-sm text-yellow-400">
                <AlertCircle className="w-4 h-4 inline mr-1" />
                Payouts typically process within 24-48 hours
              </p>
            </div>
            <Button 
              className="w-full gap-2" 
              onClick={handleRequestPayout}
              disabled={requestingPayout || !payoutRequest.amount || !payoutRequest.paypal_email}
              data-testid="submit-payout"
            >
              {requestingPayout ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Banknote className="w-4 h-4" />
                  Request Payout
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
